import classNames from 'classnames';
import he from 'he';
import fp from 'lodash/fp';
import React, { Component } from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import FontAwesome from 'react-fontawesome';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import Spinner from 'react-spinkit';
import { bindActionCreators, compose } from 'redux';
import { contentDisplayView, questionType, quizAttemptStatus, routes } from '../../../../../constants';
import { firstName, mapCpdCategoryProgress } from '../../../../../services';
import { cpdCategorysActions } from '../../../../../state/modules/areas-of-advice';
import { maxQuizAttemptsSelector } from '../../../../../state/modules/configuration';
import { contentActions } from '../../../../../state/modules/content';
import { cpdPointRelatedTasksSelector } from '../../../../../state/modules/cpd-points';
import { attemptsSelector, correctQuestionAttemptsSelector, isFetchingAttemptsSelector, isSubmittingAnswerSelector, lastSubmittedAnswerSelector, latestQuizAttemptSelector, quizActions, quizRelatedTasksSelector } from '../../../../../state/modules/quiz';
import { currentUserSelector, userActions } from '../../../../../state/modules/user';
import { noCapMap, pluralise } from '../../../../../utils';
import { ControlGroup, ControlOption, Icon, InputFormField, NumberFormField, TextAreaFormField } from '../../../../components';

class QuizQuestion extends Component {

  constructor() {
    super();

    this.initialState = (answer) => ({
      answer,
      validation: null,
      showQuestionResult: false,
      showQuizResults: false,
      hasFetchedAttempts: false,
      hasSubmittedReason: false,
      ratingState: null,
      ratingReason: null
    });

    this.state = this.initialState(this.initialAnswer(''));
  }

  resetState(answer) {
    this.setState((prevState) => ({
      ...this.initialState(answer),
      hasFetchedAttempts: prevState.hasFetchedAttempts
    }));
  }

  initialAnswer = (question) =>
    question && question.questionType === questionType.MultiChoice ?
      [] :
      '';

  componentDidMount() {
    const {
      cpdCategorys,
      isFetchingCpdCategorys,
      getCpdCategorys,
      question
    } = this.props;

    if (cpdCategorys.length === 0 && !isFetchingCpdCategorys) {
      getCpdCategorys();
    }

    this.setState({ answer: this.initialAnswer(question) });
  }

  componentWillReceiveProps(nextProps) {
    const {
      question,
      questionAttempts,
      quizAttempts
    } = this.props;

    const {
      showQuestionResult
    } = this.state;

    // Attempts are fetched every time we submit a question
    // So record the first time we fetch, and persist it
    this.setState((prevState) => ({
      hasFetchedAttempts: prevState.hasFetchedAttempts || !nextProps.isFetchingAttempts
    }));

    if (nextProps.question !== question) {
      const answer = this.initialAnswer(nextProps.question);

      // Show the next question
      // Remember to persist hasFetchedAttempts
      if (showQuestionResult) {
        this.resetState(answer);
      } else {
        this.setState({ answer });
      }
    }
    // To show the question result check if our question attempts has changed for the same quiz attempt
    // Also check if we've previously fetched any attempts though, so we can ignore the first time
    else if (!showQuestionResult && this.state.hasFetchedAttempts &&
      nextProps.questionAttempts.length !== questionAttempts.length &&
      nextProps.quizAttempts === quizAttempts) {
      this.setState({ showQuestionResult: true });
    }
  }

  userFirstName() {
    return firstName(this.props.user) || 'Anonymous';
  }

  quizRequirementsTextGenerator(passMark, quizSize, contentType) {
    if (quizSize === 1) {
      return `Answer the following question correctly to be awarded CPD for this ${fp.lowerCase(contentType)}.`
    }
    else if (passMark === quizSize) {
      return `You must answer ${passMark} questions correctly to be awarded CPD for this ${fp.lowerCase(contentType)}.`
    }
    else {
      return `You must answer ${passMark} out of ${quizSize} questions correctly to be awarded CPD for this ${fp.lowerCase(contentType)}.`
    }
  }

  quizFailureReasonTextGenerator(passMark, quizSize, contentType) {
    if (quizSize === 1) {
      return `You needed to answer the question correctly to earn CPD for this ${fp.lowerCase(contentType)}.`
    }
    else if (passMark === quizSize) {
      return `You needed to answer ${passMark} questions correctly to earn CPD for this ${fp.lowerCase(contentType)}.`
    }
    else {
      return `You needed to answer ${passMark} out of ${quizSize} questions correctly to earn CPD for this ${fp.lowerCase(contentType)}.`
    }
  }

  quizRemainingAttemptsTextGenerator(maxAttempts, attempts, isUserTasked) {
    const remainingAttempts = maxAttempts - attempts;
    if (!isUserTasked) {
      if (remainingAttempts > 1) {
        return `You have ${remainingAttempts} attempts to complete this quiz.`
      }
      else {
        return 'This is your last attempt to complete this quiz.'
      }
    } else {
      return ''
    }
  }

  professionalLearningCompleteMessageTextGenerator(points, selectedCpdCategory) {
    const worthText = selectedCpdCategory ? ` worth ${points} CPD ${pluralise('hour', points)}` : ''
    return `We've recorded it for you as professional learning${worthText}.`;
  }

  submitAnswer() {
    const {
      question,
      submitAnswer
    } = this.props;

    const {
      answer
    } = this.state;

    if (answer) {
      submitAnswer(question.id, answer);
    } else {
      this.setState({
        validation: question.questionType === questionType.MultiChoice ?
          'You must select an answer' :
          'You must type an answer'
      });
    }
  }

  onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    const {
      selectedContent,
      latestQuizAttempt,
      getQuestions,
      quizAttempts,
      maxQuizAttempts,
      saveQuizAttempt,
      goTo,
      earntMicroPoints,
      displayView,
      startQuiz,
      getUserCpdCategorys,
      taskId,
      incompleteQuizTasks,
      incompleteMicroCpdTasks
    } = this.props;

    const {
      showQuestionResult,
      showQuizResults
    } = this.state;

    if (displayView === contentDisplayView.info) {
      startQuiz();
    }
    else if (earntMicroPoints) {
      if (taskId) {
        if (incompleteMicroCpdTasks.length > 0 && fp.includes(taskId)(incompleteMicroCpdTasks)) {
          goTo(routes.app.tasks.view(taskId))
        } else {
          goTo(routes.app.tasks.home)
        }
      } else {
        goTo(routes.app.dashboard);
      }
    }
    else if (showQuestionResult) {
      if (latestQuizAttempt.quizAttempt.status === quizAttemptStatus.started) {
        // Next Question
        getQuestions(selectedContent.id);
      }
      else if (showQuizResults) {
        if (latestQuizAttempt.quizAttempt.status === quizAttemptStatus.failed &&
          quizAttempts < maxQuizAttempts) {
          // Retry
          saveQuizAttempt({ contentId: selectedContent.id });
          getQuestions(selectedContent.id);
        } else {
          if (taskId) {
            if (incompleteQuizTasks.length > 0 && fp.includes(taskId)(incompleteQuizTasks)) {
              // Go back to the incomplete task
              goTo(routes.app.tasks.view(taskId))
            } else {
              // Go back to task dashboard
              goTo(routes.app.tasks.home)
            }
          } else {
            // Go back to caddie
            goTo(routes.app.dashboard);
          }
        }
      } else {
        // See results
        this.setState({ showQuizResults: true });
        // Update user's total cpd progress
        getUserCpdCategorys();
      }
    } else {
      // Answer
      this.submitAnswer();
    }
  }

  returnToContent() {
    const {
      selectedContent,
      getQuestions,
      question
    } = this.props;

    // reset the answer in case we randomly get the same question
    if (this.state.showQuizResults) {
      this.resetState(this.initialAnswer(question));
    }

    // get a new set of questions for when the user starts a quiz again immediately
    getQuestions(selectedContent.id);

    this.props.returnToContent();
  }

  rateContent(rating, hasReason) {
    const {
      user,
      selectedContent,
      submitRating
    } = this.props;

    const {
      ratingState,
      ratingReason
    } = this.state

    if (ratingState === rating && !hasReason) {
      return
    }

    if (hasReason) {
      this.setState({ ratingState: rating, hasSubmittedReason: true })
    } else {
      this.setState({ ratingState: rating, ratingReason: null, hasSubmittedReason: false })
    }

    submitRating(
      selectedContent.id,
      {
        userId: user.id,
        contentId: selectedContent.id,
        rating: rating,
        reason: hasReason ? ratingReason : null
      }
    );
  }

  render() {
    const {
      currentQuestion,
      displayView,
      question,
      questionAttempts,
      correctAnswers,
      latestQuizAttempt,
      lastSubmittedAnswer,
      quizAttempts,
      maxQuizAttempts,
      selectedContent,
      cpdCategorys,
      earntMicroPoints,
      userSelectedCpdCategory,
      userCpdCategory,
      isSubmittingAnswer,
      taskId,
      incompleteQuizTasks,
      incompleteMicroCpdTasks,
      isUserTasked,
      industryCpdCategorys,
      publishers,
      businesses
    } = this.props;

    const {
      showQuizResults,
      showQuestionResult,
      ratingState,
      ratingReason,
      hasSubmittedReason
    } = this.state;

    const hasKnownCorrectness = (answer) => answer && answer.isCorrect != null;
    const correctClass = (answer) => showQuestionResult && lastSubmittedAnswer && hasKnownCorrectness(answer) ?
      answer.isCorrect ? 'correct' : 'incorrect' :
      null;
    const correctAnswerClass = (answer) =>
      correctClass(fp.find((selectedAnswer) =>
        answer.id === selectedAnswer.id
      )(lastSubmittedAnswer.selectedAnswers))
    const correctIcon = (answer) =>
      showQuestionResult && lastSubmittedAnswer && hasKnownCorrectness(answer) ?
        <Icon icon={`${answer.isCorrect ? 'accept' : 'close'}-thick`} /> :
        null;
    const correctAnswerIcon = (answer) =>
      correctIcon(fp.find((selectedAnswer) =>
        answer.id === selectedAnswer.id
      )(lastSubmittedAnswer.selectedAnswers));

    const maxMicroCpdPointsReached = userCpdCategory && (userCpdCategory.primaryStat.excludedMicroCpd > 0);
    const questionAttemptsHaveCorrectness = questionAttempts.some((attempt) => attempt.isCorrect != null);

    const spinnerSubmit = isSubmittingAnswer;
    const disableSubmit = (!this.state.answer || this.state.answer.length === 0) &&
      !showQuestionResult && !earntMicroPoints && displayView !== contentDisplayView.info;

    const selectedContentCpdPoints = fp.flow(
      fp.get('cpdCategorys'),
      fp.filter(
        ({ cpdCategoryId }) => {
          return fp.flow(
            fp.map('id'),
            fp.includes(cpdCategoryId)
          )(industryCpdCategorys)
        }
      ),
      fp.sumBy('points')
    )(selectedContent)

    const selectedContentCpdCategorys = fp.flow(
      fp.get('cpdCategorys'),
      fp.filter(
        ({ cpdCategoryId }) => {
          return fp.flow(
            fp.map('id'),
            fp.includes(cpdCategoryId)
          )(industryCpdCategorys)
        }
      ),
      fp.map(
        ({ cpdCategoryId }) => fp.find(
          ({ id }) => id === cpdCategoryId
        )(cpdCategorys)
      ),
      fp.map('name')
    )(selectedContent)

    const publisherName = fp.flow(
      fp.find(
        ({ id }) => id === fp.get('publisherId')(selectedContent)
      ),
      fp.get('name')
    )(publishers)

    const businessName = fp.flow(
      fp.find(
        ({ business }) => business.id === fp.get('businessId')(selectedContent)
      ),
      fp.get('business.name')
    )(businesses)

    return (
      <Grid id='quiz-question' className={classNames({
        hidden: displayView === contentDisplayView.content,
        successfulQuiz: earntMicroPoints ||
          (showQuizResults && latestQuizAttempt.quizAttempt.status === quizAttemptStatus.passed)
      })}>
        <Row>
          <Col xs={12} className='bottom-gap flexbox vertical align-center'>
            <h3 className='no-margin'>{fp.get('title')(selectedContent)}</h3>
            <div className='publisher-name'>{publisherName || businessName}</div>
            <div className='cpd-category-tags'>{selectedContentCpdCategorys.map(cpdCategory => <span key={cpdCategory}>{cpdCategory}</span>)}</div>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <form noValidate onSubmit={(e) => this.onSubmit(e)}>
              {
                displayView === contentDisplayView.info ?
                  <h2>Hey {this.userFirstName()}!</h2> :
                  earntMicroPoints || (showQuestionResult && lastSubmittedAnswer) ?
                    <h2 className={classNames({
                      incorrect: !earntMicroPoints && ((showQuizResults && latestQuizAttempt.quizAttempt.status === quizAttemptStatus.failed) ||
                        (!showQuizResults && lastSubmittedAnswer.isCorrect === false))
                    })}>
                      {
                        earntMicroPoints ?
                          'Congratulations' :
                          latestQuizAttempt.quizAttempt.status !== quizAttemptStatus.started && showQuizResults ?
                            latestQuizAttempt.quizAttempt.status === quizAttemptStatus.passed ?
                              `You got ${correctAnswers} out of ${questionAttempts.length} questions correct!` :
                              `You got ${correctAnswers} out of ${questionAttempts.length} questions correct` :
                            lastSubmittedAnswer.isCorrect === true ? 'Correct!' :
                              lastSubmittedAnswer.isCorrect === false ? 'Incorrect' : 'Answer submitted'
                      }
                    </h2> :
                    <h2>Question {currentQuestion}</h2>
              }
              {
                displayView === contentDisplayView.info ||
                  earntMicroPoints ||
                  showQuizResults ||
                  !question ?
                  null :
                  <div>
                    <p className='question'>{he.decode(question.description)}</p>
                    {
                      question.imageFileUrl ? (
                        <div className='question-image-display'><img alt="question" src={he.decode(question.imageFileUrl)} /></div>
                      ) : null
                    }
                  </div>
              }
              {
                earntMicroPoints ||
                  displayView === contentDisplayView.info ?
                  null :
                  showQuizResults && questionAttemptsHaveCorrectness ?
                    <ul className='answers'>
                      {
                        noCapMap((answer, index) =>
                          <li key={index} className={answer.isCorrect ? 'correct' : 'incorrect'}>
                            Question {index + 1} <Icon icon={`${answer.isCorrect ? 'accept' : 'close'}-thick`} />
                          </li>
                        )(questionAttempts)
                      }
                    </ul> :
                    question && question.questionType === questionType.MultiChoice ?
                      showQuestionResult ?
                        [
                          <ul className='answers' key='answers'>
                            {
                              fp.map((answer) =>
                                fp.includes(answer.id)(this.state.answer) ?
                                  <li
                                    key={answer.id}
                                    className={correctAnswerClass(answer)}>
                                    {answer.description} {correctAnswerIcon(answer)}
                                  </li> :
                                  <li key={answer.id}>{answer.description}</li>
                              )(question.answers)
                            }
                          </ul>,
                          latestQuizAttempt.quizAttempt.status === quizAttemptStatus.failed ?
                            <p key='failurehint'>
                              <Icon icon="close-thick" /> All correct answers must be selected
                            </p> :
                            null
                        ] :
                        <ControlGroup
                          control={question.multipleCorrectAnswers ? 'checkbox' : 'radio'}
                          onChange={({ target }) =>
                            ((value) => {
                              this.setState(({ answer }) => ({
                                answer: question.multipleCorrectAnswers ?
                                  fp.includes(value)(answer) ?
                                    fp.without([value])(answer) :
                                    fp.concat(answer)([value]) :
                                  [value]
                              }))
                            })(parseInt(target.value, 10))}
                          label={
                            <span id='question-info-text'>
                              Please select the most correct answer based on the content (unless directed to select more than one answer)
                            </span>
                          }
                          validation={this.state.validation}>
                          {
                            fp.map((answer) =>
                              <ControlOption
                                key={answer.id}
                                value={answer.id}
                                checked={fp.includes(answer.id)(this.state.answer)}
                                label={<span>{he.decode(answer.description)}</span>} />
                            )(question.answers)
                          }
                        </ControlGroup> :
                      question && question.questionType === questionType.ShortAnswer ?
                        showQuestionResult ?
                          <div className='text-item-answer'>
                            <label>Your answer</label>
                            <p className={correctClass(lastSubmittedAnswer)}>
                              {this.state.answer} {correctIcon(lastSubmittedAnswer)}
                            </p>
                          </div> :
                          <InputFormField
                            id='answer'
                            label='Type your answer'
                            required
                            validation={this.state.validation}
                            value={this.state.answer}
                            onChange={(e) => this.setState({ answer: e.target.value })} /> :
                        question && question.questionType === questionType.NumericAnswer ?
                          showQuestionResult ?
                            <div className='text-item-answer'>
                              <label>Your answer</label>
                              <p className={correctClass(lastSubmittedAnswer)}>
                                {this.state.answer} {correctIcon(lastSubmittedAnswer)}
                              </p>
                            </div> :
                            <NumberFormField
                              id='answer'
                              label={'Type your numeric answer'}
                              min={-1000000000}
                              max={1000000000}
                              step={0.01}
                              quantityDials={false}
                              value={this.state.answer}
                              validation={this.state.validation}
                              onChange={(e) => this.setState({ answer: e.text === '' ? '' : e.value.toString() })} /> :
                          null
              }
              {
                displayView === contentDisplayView.info ?
                  [
                    <p key={0}>
                      {
                        this.quizRequirementsTextGenerator(
                          selectedContent.quizPassMark,
                          selectedContent.quizSize,
                          selectedContent.contentType
                        )
                      }
                    </p>,
                    <p key={1}>
                      {
                        // Subtract one from the quiz attempts as we
                        // don't want to count the current attempt yet
                        this.quizRemainingAttemptsTextGenerator(
                          maxQuizAttempts,
                          quizAttempts - 1,
                          isUserTasked
                        )
                      }
                    </p>
                  ] :
                  earntMicroPoints ?
                    [
                      <p key={0}>
                        We hope you enjoyed this {fp.lowerCase(selectedContent.contentType)}.
                      </p>,
                      <p key={1}>
                        {
                          taskId ?
                            'We\'ve logged this activity in your CPD history and have marked this piece of content in your task as completed.'
                            :
                            maxMicroCpdPointsReached ?
                              'We\'ve logged this activity in your CPD history.' :
                              this.professionalLearningCompleteMessageTextGenerator(
                                selectedContent.cpdPoints,
                                userSelectedCpdCategory
                              )
                        }
                      </p>,
                      <div key={2}>
                        {
                          maxMicroCpdPointsReached || taskId ?
                            null :
                            <p>
                              Keep up the good work!
                            </p>
                        }
                      </div>,
                      <div key={3}>
                        {
                          maxMicroCpdPointsReached ?
                            <p>
                              {`As you've reach the maximum micro-CPD hours for this CPD category, this ${fp.lowerCase(selectedContent.contentType)} doesn't count towards your CPD progress.`}
                            </p> :
                            null
                        }
                      </div>
                    ] :
                    showQuizResults ?
                      latestQuizAttempt.quizAttempt.status === quizAttemptStatus.passed ?
                        taskId ?
                          <p>
                            We've logged this activity in your CPD history and have marked this piece of content in your task as completed.
                          </p> :
                          <p>
                            Nailed it! You clever thing. We've awarded {selectedContentCpdPoints} {pluralise('CPD hour', selectedContentCpdPoints)} towards your {selectedContentCpdCategorys.join(', ')} {pluralise('CPD category', selectedContentCpdCategorys.length)}.
                          </p>
                        :
                        [
                          <p key={0}>
                            {
                              quizAttempts < maxQuizAttempts ?
                                `Try again! ${this.quizFailureReasonTextGenerator(selectedContent.quizPassMark, selectedContent.quizSize, selectedContent.contentType)}` :
                                `You've just missed out! ${this.quizFailureReasonTextGenerator(selectedContent.quizPassMark, selectedContent.quizSize, selectedContent.contentType)}`
                            }
                          </p>,
                          <p key={1}>
                            {
                              quizAttempts < maxQuizAttempts ?
                                'You’ve got this.' :
                                'Pick a new content item and try again.'
                            }
                          </p>
                        ] :
                      null
              }
              {
                earntMicroPoints || (showQuizResults && latestQuizAttempt.quizAttempt.status === quizAttemptStatus.passed) ?
                  [
                    <p key={0}> How would you rate this content? </p>,
                    <p key={1}>
                      <FontAwesome className={ratingState === 'Good' ? 'rating-icon-checked' : 'rating-icon'} name='smile-o' size='2x' onClick={() => this.rateContent('Good')} />
                      <FontAwesome className={ratingState === 'Indifferent' ? 'rating-icon-checked' : 'rating-icon'} name='meh-o' size='2x' onClick={() => this.rateContent('Indifferent')} />
                      <FontAwesome className={ratingState === 'Bad' ? 'rating-icon-checked' : 'rating-icon'} name='frown-o' size='2x' onClick={() => this.rateContent('Bad')} />
                    </p>
                  ]
                  :
                  null
              }
              {
                ratingState !== null ?
                  !hasSubmittedReason ?
                    [
                      <TextAreaFormField
                        key={0}
                        id='reason'
                        labe='Reason'
                        value={he.decode(ratingReason ? ratingReason : '')}
                        onChange={(e) => this.setState({ ratingReason: he.encode(e.target.value) })} />,
                      <p key={1}>
                        <Button key={1} className='quizButton' onClick={() => this.rateContent(ratingState, true)}> Send feedBack </Button>
                      </p>
                    ] :
                    <p> Thanks for your feedback! </p> :
                  null
              }
              <Button className='quizButton' type='submit' disabled={disableSubmit || spinnerSubmit}>
                {
                  spinnerSubmit ?
                    <Spinner name='circle' className='spinner' fadeIn='none' /> :
                    displayView === contentDisplayView.info ?
                      'Start Quiz' :
                      earntMicroPoints ?
                        taskId ?
                          incompleteMicroCpdTasks.length > 0 && fp.includes(taskId)(incompleteMicroCpdTasks) ?
                            'Back to Task' :
                            'Back to Tasks' :
                          'Back to Caddie' :
                        showQuestionResult ?
                          latestQuizAttempt.quizAttempt.status === quizAttemptStatus.started ?
                            'Next Question' :
                            showQuizResults ?
                              quizAttempts < maxQuizAttempts &&
                                latestQuizAttempt.quizAttempt.status === quizAttemptStatus.failed ?
                                'Retake Quiz' :
                                taskId ?
                                  incompleteQuizTasks.length > 0 && fp.includes(taskId)(incompleteQuizTasks) ?
                                    'Back to Task' :
                                    'Back to Tasks' :
                                  'Back to Caddie' :
                              'See Results' :
                          'Answer'
                }
              </Button>
              {
                earntMicroPoints || showQuizResults ?
                  <p>
                    <Button bsStyle='plain' className='large narrow' onClick={() => this.returnToContent()}>
                      Return to {fp.lowerCase(selectedContent.contentType)}
                    </Button>
                  </p> :
                  null
              }
            </form>
          </Col>
        </Row>
      </Grid>
    );
  }
}

const mapStateToProps = (state, { isUserTasked }) => {
  const latestQuizAttempt = latestQuizAttemptSelector(state);
  const questionAttempts = (latestQuizAttempt || {}).questionAttempts || [];
  const incompleteQuizTasks = quizRelatedTasksSelector(state);
  const incompleteMicroCpdTasks = cpdPointRelatedTasksSelector(state);

  return {
    user: currentUserSelector(state),
    publishers: state.publisher.publishers,
    businesses: state.business.businesses,
    currentQuestion: questionAttempts.length + 1,
    question: fp.first(state.quiz.questions.result),
    questionAttempts: questionAttempts,
    isFetchingAttempts: isFetchingAttemptsSelector(state),
    isSubmittingAnswer: isSubmittingAnswerSelector(state),
    correctAnswers: latestQuizAttempt && latestQuizAttempt.correctAnswers != null ?
      latestQuizAttempt.correctAnswers :
      (correctQuestionAttemptsSelector(state) || []).length,
    latestQuizAttempt,
    incompleteQuizTasks,
    incompleteMicroCpdTasks,
    lastSubmittedAnswer: lastSubmittedAnswerSelector(state),
    quizAttempts: attemptsSelector(state).length,
    isUserTasked,
    maxQuizAttempts: isUserTasked ? (attemptsSelector(state).length + 1) : maxQuizAttemptsSelector(state),
    selectedContent: state.content.selectedContent,
    cpdCategorys: state.cpdCategorys.cpdCategorys,
    isFetchingCpdCategorys: state.cpdCategorys.isFetching,
    industryCpdCategorys: state.industry.cpdCategorys,
    userSelectedCpdCategory: !state.cpdCategorys.isFetching && !state.user.isFetchingCpdCategoryProgress ?
      fp.some(
        (cpdCategory) => fp.flow(
          fp.get('cpdCategorys'),
          fp.map('cpdCategoryId'),
          fp.includes(cpdCategory.cpdCategoryId)
        )(state.content.selectedContent)
      )(mapCpdCategoryProgress(state.user.cpdCategoryProgress.cpdCategorys)) :
      null,
    userCpdCategory: state.user.cpdCategoryProgress ? fp.find(
      (cpdCategory) => fp.flow(
        fp.get('cpdCategorys'),
        fp.map('cpdCategoryId'),
        fp.includes(cpdCategory.cpdCategoryId)
      )(state.content.selectedContent)
    )(mapCpdCategoryProgress(state.user.cpdCategoryProgress.cpdCategorys)) : undefined
  };
};

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch),
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getUserCpdCategorys: bindActionCreators(userActions.user.getCpdCategorys, dispatch),
  getQuizAttempts: bindActionCreators(quizActions.quiz.attempts.get, dispatch),
  getQuestions: bindActionCreators(quizActions.quiz.questions.get, dispatch),
  submitAnswer: bindActionCreators(quizActions.quiz.questionAttempts.submitAnswer, dispatch),
  saveQuizAttempt: bindActionCreators(quizActions.quiz.attempts.save, dispatch),
  submitRating: bindActionCreators(contentActions.content.rateContent, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(QuizQuestion);



// WEBPACK FOOTER //
// ./src/containers/pages/app/training/partials/quiz-question.js